import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

// Context for the paid-replacement checkout (`/reserve/replacement`). Set by the
// "Report a problem" flow when a driver accepts a replacement offer that costs
// more than their original booking value (`amount_due > 0`). Free replacements
// confirm inline and never touch this store.
export interface ReplacementContext {
  // The ORIGINAL booking being replaced — the replacement endpoints are keyed
  // off it, not the new hold.
  booking_id: string;
  booking_confirmation_code: string;
  hold_id: string;
  listing_id: string;
  // Vehicle stays the one on the original booking — shown read-only at checkout.
  vehicle_id: string;
  listing_name: string | null;
  listing_address: string | null;
  starts_at: string;
  ends_at: string;
  expires_at: string;
  // Held-spot quote (integer cents).
  base_amount: number;
  fee_amount: number;
  tax_amount: number;
  total_amount: number;
  // What the driver actually owes after their original booking value is applied.
  amount_due: number;
  // Credit applied from the original booking (display only).
  applied_amount: number | null;
  currency: string;
  distance_difference_miles: number | null;
  /**
   * The spot's IANA zone, carried from the offer's `spot_details` so the
   * replacement window renders on the same clock as the booking it replaces.
   * Absent against a backend predating the field; formatters fall back to
   * viewer-local.
   */
  timezone?: string | null;
  // Amenities from the offer's `spot_details` for the checkout summary card.
  spot_type?: string;
  ev_charger?: boolean;
  gated?: boolean;
  security_camera?: boolean;
  /**
   * Set once checkout has asked payment setup, so a reload asks again instead
   * of showing a fresh form.
   */
  attempted?: boolean;
}

interface ReplacementStore {
  context: ReplacementContext | null;
  setContext: (ctx: ReplacementContext) => void;
  /** Merge into the current context; a no-op once it has been cleared. */
  update: (patch: Partial<ReplacementContext>) => void;
  clear: () => void;
}

export const useReplacementStore = create<ReplacementStore>()(
  persist(
    (set) => ({
      context: null,
      setContext: (context) => set({ context }),
      update: (patch) =>
        set(({ context }) => ({
          context: context ? { ...context, ...patch } : null,
        })),
      clear: () => set({ context: null }),
    }),
    {
      name: "ezspot_replacement_hold",
      storage: createJSONStorage(() => sessionStorage),
      onRehydrateStorage: () => (state) => {
        // Drop a stale context whose hold has already expired server-side.
        if (
          state?.context &&
          new Date(state.context.expires_at).getTime() <= Date.now()
        ) {
          state.clear();
        }
      },
    },
  ),
);
